/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation, useNavigate } from '@remix-run/react'
import { type Dispatch, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useCurrentLanguage } from '#app/hooks/use-current-language.tsx'
import { type Action } from '#types/chat/index.ts'

interface StreamingUpdateProps {
	isCurrentlyStreaming: boolean
	conversationId: string
	dispatch: Dispatch<Action>
	eventSourceRef: any
	revalidator: {
		revalidate: () => void
		state: 'idle' | 'loading'
	}
}

export function useStreamingUpdates({
	isCurrentlyStreaming,
	conversationId,
	dispatch,
	eventSourceRef,
	revalidator,
}: StreamingUpdateProps) {
	const location = useLocation()
	const navigate = useNavigate()
	const currentLanguage = useCurrentLanguage()

	const isNewChatPath = location.pathname === `/${currentLanguage}/chat`

	useEffect(() => {
		if (isCurrentlyStreaming) {
			const newAssistantMessageId = uuidv4()
			dispatch({
				type: 'CREATE_MESSAGE',
				role: 'assistant',
				id: newAssistantMessageId,
				conversationId,
				newUpdatedAt: new Date().toISOString(),
			})

			if (eventSourceRef.current) {
				eventSourceRef.current.close()
				eventSourceRef.current = null
			}

			const eventSource = new EventSource(
				`/completion?conversationId=${conversationId}&messageId=${newAssistantMessageId}`,
			)

			eventSource.addEventListener('message', event => {
				if (event.data.startsWith('[DONE]')) {
					eventSource.close()

					const tokensUsed = event.data.split('&tokensUsed=')[1]
					const tokensUsedNumber = parseInt(tokensUsed, 10)

					dispatch({
						type: 'SET_IS_CURRENTLY_STREAMING',
						isCurrentlyStreaming: false,
						numberOfTokens: tokensUsedNumber,
					})

					if (isNewChatPath) {
						navigate(`${location.pathname}/${conversationId}`, {
							preventScrollReset: true,
						})
					}
					revalidator.revalidate()
				} else {
					dispatch({
						type: 'SET_STREAMING_MESSAGE',
						currentMessageId: newAssistantMessageId,
						payload: JSON.parse(event.data),
					})
				}
			})

			eventSource.addEventListener('error', event => {
				eventSource.close()
				dispatch({
					type: 'SET_IS_CURRENTLY_STREAMING',
					isCurrentlyStreaming: false,
				})
			})

			eventSourceRef.current = eventSource

			return () => {
				if (eventSourceRef.current) {
					eventSourceRef.current.close()
					eventSourceRef.current = null
				}
			}
		}
	}, [conversationId, dispatch, isCurrentlyStreaming, revalidator])
}
